import type { Instant } from '@js-joda/core';
import type { ReadonlySignal } from '@preact/signals-react';
import type { SubscriptionStatus } from '@thinkalpha/access-ws-client/contracts/subscription.js';
import type { UserDetails } from '@thinkalpha/ui-types/types/user';

export const HELP_CENTER_ROLE = 'features::help-center';
export const AI_ASSISTANT_ROLE = 'features::ai-assistant';

/**
 * Access to a feature that needs both its feature flag (the kill switch / rollout control) and its entitlement role.
 * `isReady` is false only while the flag is on and the user's roles are still loading.
 */
export interface FeatureAccess {
    readonly isEnabled: boolean;
    readonly isReady: boolean;
}

export interface CurrentUserModel extends Disposable {
    readonly user: ReadonlySignal<UserDetails | undefined>;
    readonly userName: ReadonlySignal<string>;
    readonly userEmail: ReadonlySignal<string | undefined>;
    readonly userId: ReadonlySignal<string | undefined>;
    readonly isMasquerading: ReadonlySignal<boolean>;
    readonly isSuperuser: ReadonlySignal<boolean>;
    readonly isSuperUserModeEnabled: ReadonlySignal<boolean>;
    readonly isSuperUserAlertingEnabled: ReadonlySignal<boolean>;
    readonly masqExpires: ReadonlySignal<Instant | undefined>;
    readonly isDeveloper: ReadonlySignal<boolean>;
    readonly canToggleSuperUserAlerting: ReadonlySignal<boolean>;
    readonly entitlementsRevision: ReadonlySignal<number>;
    readonly isSubscriptionUser: ReadonlySignal<boolean>;
    /** True once the security package (access list and roles) has loaded or failed. */
    readonly isSubscriptionUserReady: ReadonlySignal<boolean>;
    /** Summary of the user's main subscriptions; undefined until the security package has loaded. */
    readonly subscriptionStatus: ReadonlySignal<SubscriptionStatus | 'none' | undefined>;
    /** Cheap, security-package-based eligibility for showing the lazy Firms control trigger. */
    readonly isFirmsControlEligible: ReadonlySignal<boolean>;
    /** `enable-in-app-help` flag plus the {@link HELP_CENTER_ROLE} role, or active superuser mode. */
    readonly helpCenterAccess: ReadonlySignal<FeatureAccess>;
    /** `enable-global-assistant` flag plus the {@link AI_ASSISTANT_ROLE} role, or active superuser mode. */
    readonly aiAssistantAccess: ReadonlySignal<FeatureAccess>;
    toggleSuperUserMode(enabled: boolean): void;
    toggleSuperUserAlerting(enabled: boolean): void;
    notifyEntitlementsChanged(): void;
    stopMasq(): void;
}
